import * as React from "react"
import { Head, Link, router, usePage } from "@inertiajs/react"
import { ArrowDown, ArrowUp, ChevronsUpDown } from "lucide-react"
import { AdminShell } from "@/components/AdminShell"
import { PageHeader } from "@/components/PageHeader"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { NavTabs } from "@/components/ui/nav-tabs"
import { Select } from "@/components/ui/select"
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeaderCell,
  TableRow,
} from "@/components/ui/table"
import {
  AUDIT_TABS,
  AuditStatusBadge,
  CopyLinkButton,
  KIND_LABELS,
  formatDate,
  formatDateTime,
  type AuditStatus,
  type AuditSummary,
} from "@/components/admin/audits"

const DESCRIPTION =
  "Every hosted audit deck, with its status, opens, version and expiry. Sort, filter and edit in place."

// --- sorting ---------------------------------------------------------------
// Client-side throughout: audits are per-prospect, so this is tens of rows, and
// sorting them in the browser is instant and costs no round trip.

type SortKey =
  | "client_name"
  | "kind"
  | "status"
  | "opens"
  | "last_seen_at"
  | "version_count"
  | "slide_count"
  | "expires_at"
  | "created_at"

type Direction = "asc" | "desc"

// Status is a derived label, not a scalar, so it sorts by how urgent it is
// rather than alphabetically. "Still unopened" is what changes what Matt does
// next, so it leads.
const STATUS_ORDER: Record<AuditStatus, number> = {
  not_opened: 0,
  opened: 1,
  expired: 2,
  revoked: 3,
  deck_deleted: 4,
}

function sortValue(audit: AuditSummary, key: SortKey): string | number {
  switch (key) {
    case "client_name":
      return audit.client_name.toLowerCase()
    // Unset sorts last under ascending, so the named kinds group together.
    case "kind":
      return audit.kind ?? "zzz"
    case "status":
      return STATUS_ORDER[audit.status] ?? 99
    case "opens":
      return audit.opens
    case "version_count":
      return audit.version_count
    case "slide_count":
      return audit.slide_count ?? -1
    // Dates compare correctly as ISO strings. A null last_seen sorts as the
    // earliest possible, so "never seen" collects at one end rather than
    // scattering.
    case "last_seen_at":
      return audit.last_seen_at ?? ""
    case "expires_at":
      return audit.expires_at
    case "created_at":
      return audit.created_at
  }
}

// --- inline editing --------------------------------------------------------

type EditField = "client_name" | "kind" | "expires_at"

type Editing = { id: number; field: EditField }

/** Commits one field of one audit. preserveState is what keeps the sort, filters
 *  and search alive across the redirect — without it every edit would throw away
 *  the view the user just set up. */
function commit(id: number, field: EditField, value: string, done: () => void) {
  router.patch(
    `/admin/audits/${id}`,
    { [field]: value },
    { preserveState: true, preserveScroll: true, onFinish: done },
  )
}

function EditableCell({
  audit,
  field,
  editing,
  setEditing,
  children,
}: {
  audit: AuditSummary
  field: EditField
  editing: Editing | null
  setEditing: (next: Editing | null) => void
  children: React.ReactNode
}) {
  const active = editing?.id === audit.id && editing.field === field
  // Escape has to beat the blur that follows it. Clearing el.onblur would not
  // work — React attaches its handlers by delegation at the root, not as a DOM
  // property — so the blur handler checks this instead.
  const cancelled = React.useRef(false)

  if (!active) {
    return (
      <button
        type="button"
        onClick={() => {
          cancelled.current = false
          setEditing({ id: audit.id, field })
        }}
        className="-mx-1 w-full rounded px-1 py-0.5 text-left hover:bg-surface"
        title="Click to edit"
      >
        {children}
      </button>
    )
  }

  const close = () => setEditing(null)

  const save = (value: string) => {
    if (cancelled.current) return
    commit(audit.id, field, value, close)
  }

  const cancel = () => {
    cancelled.current = true
    close()
  }

  if (field === "kind") {
    return (
      <Select
        autoFocus
        defaultValue={audit.kind ?? ""}
        onBlur={close}
        onChange={(event) => save(event.target.value)}
        onKeyDown={(event) => {
          if (event.key === "Escape") cancel()
        }}
      >
        <option value="">No kind</option>
        {Object.entries(KIND_LABELS).map(([value, label]) => (
          <option key={value} value={value}>
            {label}
          </option>
        ))}
      </Select>
    )
  }

  const isDate = field === "expires_at"
  return (
    <Input
      autoFocus
      type={isDate ? "date" : "text"}
      defaultValue={isDate ? audit.expires_at.slice(0, 10) : audit.client_name}
      onBlur={(event) => save(event.target.value)}
      onKeyDown={(event) => {
        if (event.key === "Enter") event.currentTarget.blur()
        if (event.key === "Escape") cancel()
      }}
    />
  )
}

// --- sortable header -------------------------------------------------------

function SortableHeader({
  label,
  sortKey,
  sort,
  setSort,
  align,
}: {
  label: string
  sortKey: SortKey
  sort: { key: SortKey; direction: Direction }
  setSort: (next: { key: SortKey; direction: Direction }) => void
  align?: "right"
}) {
  const active = sort.key === sortKey
  const Icon = !active ? ChevronsUpDown : sort.direction === "asc" ? ArrowUp : ArrowDown

  return (
    <TableHeaderCell className={align === "right" ? "text-right" : undefined}>
      <button
        type="button"
        aria-sort={active ? (sort.direction === "asc" ? "ascending" : "descending") : "none"}
        onClick={() =>
          setSort({
            key: sortKey,
            direction: active && sort.direction === "asc" ? "desc" : "asc",
          })
        }
        // uppercase is restated here: TableHeaderCell sets it on the <th>, but a
        // <button> child does not pick it up, which left the sortable headers
        // in title case beside the plain ones.
        className={`inline-flex items-center gap-1 uppercase hover:text-ink-display ${
          align === "right" ? "flex-row-reverse" : ""
        }`}
      >
        {label}
        <Icon aria-hidden="true" className={`h-3 w-3 ${active ? "" : "opacity-40"}`} />
      </button>
    </TableHeaderCell>
  )
}

// --- page ------------------------------------------------------------------

const STATUS_FILTERS: { value: string; label: string }[] = [
  { value: "", label: "All statuses" },
  { value: "not_opened", label: "Not opened" },
  { value: "opened", label: "Opened" },
  { value: "expired", label: "Expired" },
  { value: "revoked", label: "Revoked" },
  { value: "deck_deleted", label: "Deck deleted" },
]

export default function AdminAuditsIndex({ audits }: { audits: AuditSummary[] }) {
  const { url } = usePage()
  const [sort, setSort] = React.useState<{ key: SortKey; direction: Direction }>({
    key: "created_at",
    direction: "desc", // matches the controller's newest_first default
  })
  const [status, setStatus] = React.useState("")
  const [kind, setKind] = React.useState("")
  const [search, setSearch] = React.useState("")
  const [editing, setEditing] = React.useState<Editing | null>(null)

  const visible = React.useMemo(() => {
    const term = search.trim().toLowerCase()
    const filtered = audits.filter((audit) => {
      if (status && audit.status !== status) return false
      if (kind && (audit.kind ?? "") !== kind) return false
      if (term && !audit.client_name.toLowerCase().includes(term)) return false
      return true
    })

    return [...filtered].sort((a, b) => {
      const left = sortValue(a, sort.key)
      const right = sortValue(b, sort.key)
      if (left === right) return 0
      const order = left < right ? -1 : 1
      return sort.direction === "asc" ? order : -order
    })
  }, [audits, status, kind, search, sort])

  const filtering = Boolean(status || kind || search.trim())

  return (
    <>
      <Head title="Audits">
        <meta name="description" content={DESCRIPTION} />
        <meta property="og:title" content="Audits" />
        <meta property="og:description" content={DESCRIPTION} />
      </Head>
      <AdminShell wide>
        <PageHeader
          title="Audits"
          description={
            filtering
              ? `${visible.length} of ${audits.length} shown`
              : `${audits.length} ${audits.length === 1 ? "audit" : "audits"}`
          }
          actions={
            <Button asChild>
              <Link href="/admin/audits/new">New audit</Link>
            </Button>
          }
        />
        <NavTabs tabs={AUDIT_TABS} current={url} label="Audits" className="mt-6" />

        {audits.length === 0 ? (
          <div className="mt-6 flex h-40 items-center justify-center rounded-lg border border-hairline bg-page">
            <p className="text-sm text-ink-muted">
              No audits yet. Upload a built deck to publish one.
            </p>
          </div>
        ) : (
          <>
            <div className="mt-6 flex flex-wrap items-center gap-3">
              <div className="w-full sm:w-64">
                <label htmlFor="audit-search" className="sr-only">
                  Search by client
                </label>
                <Input
                  id="audit-search"
                  type="search"
                  placeholder="Search client…"
                  value={search}
                  onChange={(event) => setSearch(event.target.value)}
                />
              </div>
              <div className="w-full sm:w-44">
                <label htmlFor="audit-status" className="sr-only">
                  Filter by status
                </label>
                <Select
                  id="audit-status"
                  value={status}
                  onChange={(event) => setStatus(event.target.value)}
                >
                  {STATUS_FILTERS.map((option) => (
                    <option key={option.value} value={option.value}>
                      {option.label}
                    </option>
                  ))}
                </Select>
              </div>
              <div className="w-full sm:w-44">
                <label htmlFor="audit-kind" className="sr-only">
                  Filter by kind
                </label>
                <Select
                  id="audit-kind"
                  value={kind}
                  onChange={(event) => setKind(event.target.value)}
                >
                  <option value="">All kinds</option>
                  {Object.entries(KIND_LABELS).map(([value, label]) => (
                    <option key={value} value={value}>
                      {label}
                    </option>
                  ))}
                </Select>
              </div>
              {filtering && (
                <Button
                  variant="ghost"
                  onClick={() => {
                    setSearch("")
                    setStatus("")
                    setKind("")
                  }}
                >
                  Clear
                </Button>
              )}
            </div>

            {/* overflow-hidden for the rounded corners; the Table primitive
                already wraps itself in its own overflow-x-auto scroller. */}
            <div className="mt-4 overflow-hidden rounded-lg border border-hairline bg-page">
              <Table>
                <TableHead>
                  <TableRow>
                    <SortableHeader label="Client" sortKey="client_name" sort={sort} setSort={setSort} />
                    <SortableHeader label="Kind" sortKey="kind" sort={sort} setSort={setSort} />
                    <SortableHeader label="Status" sortKey="status" sort={sort} setSort={setSort} />
                    <SortableHeader label="Opens" sortKey="opens" sort={sort} setSort={setSort} align="right" />
                    <SortableHeader label="Last seen" sortKey="last_seen_at" sort={sort} setSort={setSort} />
                    <SortableHeader label="Version" sortKey="version_count" sort={sort} setSort={setSort} />
                    <SortableHeader label="Slides" sortKey="slide_count" sort={sort} setSort={setSort} align="right" />
                    <SortableHeader label="Expires" sortKey="expires_at" sort={sort} setSort={setSort} />
                    <SortableHeader label="Published" sortKey="created_at" sort={sort} setSort={setSort} />
                    <TableHeaderCell>Link</TableHeaderCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {visible.map((audit) => (
                    <TableRow key={audit.id} className="group">
                      <TableCell>
                        <div className="flex items-center gap-2">
                          <EditableCell
                            audit={audit}
                            field="client_name"
                            editing={editing}
                            setEditing={setEditing}
                          >
                            <span className="font-medium text-ink-display">
                              {audit.client_name}
                            </span>
                          </EditableCell>
                          {/* Clicking the name edits it, so opening the audit
                              needs its own control. Revealed on hover from sm
                              up, so rows of "Open" do not compete with the
                              names — but always visible on touch, where there
                              is no hover and it would otherwise be unreachable. */}
                          <Link
                            href={`/admin/audits/${audit.id}`}
                            className="whitespace-nowrap text-xs text-ink-muted transition-opacity focus:opacity-100 sm:opacity-0 sm:group-hover:opacity-100"
                          >
                            Open
                          </Link>
                        </div>
                      </TableCell>
                      <TableCell>
                        <EditableCell
                          audit={audit}
                          field="kind"
                          editing={editing}
                          setEditing={setEditing}
                        >
                          {audit.kind ? (
                            (KIND_LABELS[audit.kind] ?? audit.kind)
                          ) : (
                            <span className="text-ink-muted">—</span>
                          )}
                        </EditableCell>
                      </TableCell>
                      <TableCell>
                        <AuditStatusBadge status={audit.status} />
                      </TableCell>
                      <TableCell className="text-right tabular-nums">
                        {audit.opens > 0 ? audit.opens : <span className="text-ink-muted">—</span>}
                      </TableCell>
                      <TableCell className="whitespace-nowrap text-ink-muted">
                        {audit.last_seen_at ? formatDateTime(audit.last_seen_at) : "—"}
                      </TableCell>
                      <TableCell className="whitespace-nowrap tabular-nums">
                        v{audit.version_count}
                      </TableCell>
                      <TableCell className="text-right tabular-nums">
                        {audit.slide_count ?? <span className="text-ink-muted">—</span>}
                      </TableCell>
                      <TableCell className="whitespace-nowrap text-ink-muted">
                        <EditableCell
                          audit={audit}
                          field="expires_at"
                          editing={editing}
                          setEditing={setEditing}
                        >
                          {formatDate(audit.expires_at)}
                        </EditableCell>
                      </TableCell>
                      <TableCell className="whitespace-nowrap text-ink-muted">
                        {formatDate(audit.created_at)}
                      </TableCell>
                      <TableCell>
                        <CopyLinkButton url={audit.url} />
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>

            {visible.length === 0 && (
              <div className="mt-4 flex h-24 items-center justify-center rounded-lg border border-hairline bg-page">
                <p className="text-sm text-ink-muted">
                  No audits match those filters.
                </p>
              </div>
            )}
          </>
        )}
      </AdminShell>
    </>
  )
}
